/**
 * Datenblätter: ein Ordner am Server, an Modul und Komponente nur die
 * Verknüpfung. Die Liste liegt im lokalen Zustand (geholt beim Abgleich), damit
 * auch ohne Empfang zu sehen ist, welches Datenblatt dazugehört. Die Datei
 * selbst braucht Verbindung.
 */

import { useLiveQuery } from 'dexie-react-hooks';

import { DATENBLAETTER, rufeServer } from './abgleich.ts';
import { db } from './db.ts';
import { sichererBlob } from './schritte.ts';

export interface Datenblatt {
  id: string;
  titel: string;
  hersteller: string | null;
  dateiname: string | null;
  mime: string;
  groesse_bytes: number;
  angelegt_am: string;
}

export function useDatenblaetter(): Datenblatt[] {
  return useLiveQuery(
    () => db.zustand.get(DATENBLAETTER).then((z) => (z?.wert as Datenblatt[] | undefined) ?? []),
    [],
    [] as Datenblatt[],
  );
}

export function datenblattname(d: Datenblatt): string {
  return d.hersteller ? `${d.hersteller} – ${d.titel}` : d.titel;
}

async function holeListe(): Promise<void> {
  const antwort = await rufeServer('/api/datenblaetter');
  if (!antwort.ok) throw new Error(`Liste nicht geholt (${antwort.status})`);
  const { datenblaetter } = (await antwort.json()) as { datenblaetter: Datenblatt[] };
  await db.zustand.put({ schluessel: DATENBLAETTER, wert: datenblaetter });
}

export async function ladeDatenblattHoch(datei: File, titel: string, hersteller: string): Promise<void> {
  const abfrage = new URLSearchParams({ titel, name: datei.name });
  if (hersteller.trim()) abfrage.set('hersteller', hersteller.trim());
  const antwort = await rufeServer(`/api/datenblaetter?${abfrage}`, {
    method: 'POST',
    headers: { 'Content-Type': datei.type || 'application/pdf' },
    body: datei,
  });
  if (!antwort.ok) {
    const { fehler } = (await antwort.json().catch(() => ({}))) as { fehler?: string };
    throw new Error(fehler ?? `Hochladen fehlgeschlagen (${antwort.status})`);
  }
  await holeListe();
}

export async function entferneDatenblatt(id: string): Promise<void> {
  const antwort = await rufeServer(`/api/datenblaetter/${id}`, { method: 'DELETE' });
  if (!antwort.ok) throw new Error(`Entfernen fehlgeschlagen (${antwort.status})`);
  await holeListe();
}

/**
 * Öffnet das Datenblatt in einem neuen Fenster. Das Fenster wird sofort beim
 * Tippen aufgemacht und erst danach befüllt — sonst hält Safari es für ein
 * ungebetenes Popup, weil zwischen Tipp und Öffnen ein Abruf liegt.
 */
export async function oeffneDatenblatt(id: string): Promise<void> {
  const fenster = window.open('', '_blank');
  try {
    const antwort = await rufeServer(`/api/datenblaetter/${id}/datei`);
    if (!antwort.ok) throw new Error(`Datenblatt nicht geladen (${antwort.status})`);
    // Wie bei den Dokumenten: Eine Blob-Adresse läuft unter unserer Herkunft.
    const url = URL.createObjectURL(sichererBlob(await antwort.blob()));
    if (fenster) fenster.location.href = url;
    else window.location.href = url;
    // Freigeben, wenn das Fenster die Datei sicher geladen hat.
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (fehler) {
    fenster?.close();
    throw fehler;
  }
}
